import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, ArrowRightLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supplierProductsAPI } from '../lib/api';

const normalize = (s) => (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/**
 * Dialog to re-attach a supplier product to a different industrial product.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - supplierProduct: { id, supplier_name_product, brand, industrial_product_id, supplier_name }
 *  - currentIndustrialProductName: string
 *  - industrialProducts: Array<{ id, name, family, category }>
 *  - onMoved: (newIndustrialProductId) => void
 */
export default function MoveSupplierProductDialog({
    open,
    onClose,
    supplierProduct,
    currentIndustrialProductName = '',
    industrialProducts = [],
    onMoved,
}) {
    const [query, setQuery] = useState('');
    const [selectedId, setSelectedId] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            setQuery('');
            setSelectedId('');
        }
    }, [open]);

    const filtered = useMemo(() => {
        const q = normalize(query);
        const list = industrialProducts
            .filter(p => p.id !== supplierProduct?.industrial_product_id)
            .filter(p => !q || normalize(p.name).includes(q) || normalize(p.family).includes(q) || normalize(p.category).includes(q))
            .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr'));
        return list.slice(0, 200);
    }, [industrialProducts, query, supplierProduct]);

    const handleConfirm = async () => {
        if (!selectedId) {
            toast.error('Sélectionnez un produit industriel cible');
            return;
        }
        setSaving(true);
        try {
            await supplierProductsAPI.move(supplierProduct.id, selectedId);
            toast.success('Produit fournisseur rattaché');
            onMoved?.(selectedId);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors du déplacement');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl flex flex-col max-h-[85vh]" data-testid="move-supplier-product-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ArrowRightLeft className="w-5 h-5 text-orange-500" />
                        Changer de produit industriel
                    </DialogTitle>
                    <DialogDescription>
                        Rattacher <span className="font-semibold text-slate-700">{supplierProduct?.supplier_name_product || supplierProduct?.supplier_ref || '-'}</span>
                        {' '}à un autre produit industriel.
                    </DialogDescription>
                </DialogHeader>

                <div className="text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 flex-shrink-0">
                    <span className="font-semibold text-slate-500">Actuellement rattaché à :</span>{' '}
                    <span className="font-semibold text-slate-800">{currentIndustrialProductName || '-'}</span>
                </div>

                <div className="relative flex-shrink-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        autoFocus
                        placeholder="Rechercher un produit industriel..."
                        className="pl-9"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        data-testid="move-search-input"
                    />
                </div>

                <div className="flex-1 min-h-0 overflow-auto border border-slate-200 rounded-lg">
                    {filtered.length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-400">
                            Aucun produit industriel trouvé.
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {filtered.map((p) => {
                                const active = selectedId === p.id;
                                return (
                                    <li
                                        key={p.id}
                                        className={`px-3 py-2 cursor-pointer text-sm transition-colors ${
                                            active ? 'bg-orange-50 ring-1 ring-orange-200' : 'hover:bg-slate-50'
                                        }`}
                                        onClick={() => setSelectedId(p.id)}
                                        data-testid={`move-target-${p.id}`}
                                    >
                                        <div className="font-semibold text-slate-700 truncate">{p.name}</div>
                                        <div className="text-[11px] text-slate-400 truncate">
                                            {p.category || '—'} {p.family ? `· ${p.family}` : ''}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <DialogFooter className="flex-shrink-0">
                    <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={!selectedId || saving}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="confirm-move-btn"
                    >
                        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Déplacement...</> : <><ArrowRightLeft className="w-4 h-4 mr-2" />Déplacer</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
